import { useAuth } from '../context/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import api from '../api/api';

export default function Header() {
    const { isAuthenticated, user, logout } = useAuth();
    const [showMenu, setShowMenu] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [allRecipes, setAllRecipes] = useState([]);
    const [recipesLoaded, setRecipesLoaded] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const navLinks = [
        { to: '/idees-de-repas', label: 'Idées de recettes' },
        ////{ to: '/blog', label: 'Blog' }
    ];

    // Télécharger toutes les recettes au chargement du composant
    useEffect(() => {
        const fetchAllRecipes = async () => {
            if (isAuthenticated && !recipesLoaded) {
                try {
                    const response = await api.post('/api/meals/recipes');
                    if (response.data.success) {
                        setAllRecipes(response.data.recipes || []);
                        setRecipesLoaded(true);
                    }
                } catch (error) {
                    console.error('Erreur lors du chargement des recettes:', error);
                }
            }
        };

        fetchAllRecipes();
    }, [isAuthenticated, recipesLoaded]);

    const handleLogout = () => {
        logout();
        setShowMenu(false);
        // Réinitialiser les recettes lors de la déconnexion
        setAllRecipes([]);
        setRecipesLoaded(false);
    };

    const handleSearch = (query) => {
        setSearchQuery(query);
        
        if (query.trim().length < 2) {
            setSearchResults([]);
            setShowResults(false);
            return;
        }

        // Filtrer les recettes localement
        const filteredRecipes = allRecipes.filter(recipe => 
            recipe.title.toLowerCase().includes(query.toLowerCase()) ||
            recipe.description.toLowerCase().includes(query.toLowerCase()) ||
            (recipe.dishType && recipe.dishType.toLowerCase().includes(query.toLowerCase())) ||
            (recipe.ingredients && recipe.ingredients.some(ingredient => 
                ingredient.name.toLowerCase().includes(query.toLowerCase())
            ))
        );

        setSearchResults(filteredRecipes.slice(0, 10)); // Limiter à 10 résultats
        setShowResults(true);
    };

    const handleResultClick = (recipe) => {
        setShowResults(false);
        setSearchQuery('');
        navigate(`/recette/${recipe.id}`);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && searchQuery.trim()) {
            // Rediriger vers une page de résultats si nécessaire
            navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
            setShowResults(false);
        }
    };

    return (
      <>
        <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
            <Link to="/">
              <img src="/img/logo_color-min.png" alt="YAQAM" className="h-16" />
            </Link>
            </div>
            <div className="hidden md:flex items-center space-x-6 ml-6">
              {navLinks.map((link) => {
                const isActive = location.pathname.startsWith(link.to);
                const isIngredientLink = link.to.startsWith('/idees-de-repas/idees-recettes');
                const externalProps = isIngredientLink
                  ? { target: '_blank', rel: 'noopener noreferrer' }
                  : {};
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    {...externalProps}
                    className={`text-sm font-medium transition-colors ${
                      isActive ? 'text-[#E67E22]' : 'text-[#2C3E50] hover:text-[#E67E22]'
                    }`}
                    onClick={() => setShowMenu(false)}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>
            
            {/* Barre de recherche - cachée sur mobile, sur la homepage et sur routes non protégées */}
            {isAuthenticated && location.pathname !== '/' && (
            <div className="relative flex-1 mx-8 max-w-2xl hidden md:block">
            <div className="search-bar flex">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: '#6c757d' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input 
              type="text" 
              placeholder="Rechercher des recettes, ingrédients..." 
              className="w-full"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onKeyPress={handleKeyPress}
              onFocus={() => searchResults.length > 0 && setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)}
              style={{ color: '#2C3E50' }}
            />
            </div>

            {/* Résultats de recherche - Desktop */}
            {showResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-96 overflow-y-auto z-50">
              {searchResults.map((recipe) => (
              <div
                key={recipe.id}
                onClick={() => handleResultClick(recipe)}
                className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div className="flex items-center">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 mr-3">
                  {recipe.imgUrlArray && recipe.imgUrlArray.length > 0 ? (
                  <img
                    src={recipe.imgUrlArray[0]}
                    alt={recipe.title}
                    className="w-full h-full object-cover"
                  />
                  ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#6c757d' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  )}
                </div>
                <div className="flex-grow">
                  <h4 className="font-medium text-sm" style={{ color: '#2C3E50' }}>{recipe.title}</h4>
                  <p className="text-xs line-clamp-1" style={{ color: '#6c757d' }}>{recipe.description}</p>
                </div>
                </div>
              </div>
              ))}
            </div>
            )}
          </div>
          )}
          
          {/* Actions - Profil visible sur mobile et desktop */}
          <div className="flex items-center space-x-6">
          {isAuthenticated ? (
            <>
            {/* Menu utilisateur - Mobile et Desktop */}
            <div className="relative">
              <button 
              onClick={() => setShowMenu(!showMenu)}
              className="flex items-center space-x-2 focus:outline-none"
              >
              <span className="text-sm font-medium hidden md:block" style={{ color: '#2C3E50' }}>{user?.name || 'User'}</span>
              <span className="text-white rounded-full p-1" style={{ backgroundColor: '#87A96B' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </span>
              </button>

              {/* Menu déroulant */}
              {showMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 border border-gray-100 z-50">
                <Link
                to="/dashboard"
                className="block px-4 py-2 text-sm hover:bg-gray-100"
                style={{ color: '#2C3E50' }}
                onClick={() => setShowMenu(false)}
                >
                Acceuil
                </Link>
                <Link
                to="/idees-de-repas"
                className="block px-4 py-2 text-sm hover:bg-gray-100"
                style={{ color: '#2C3E50' }}
                onClick={() => setShowMenu(false)}
                >
                Idées de recettes
                </Link>
                {/* <Link
                to="/blog"
                className="block px-4 py-2 text-sm hover:bg-gray-100"
                style={{ color: '#2C3E50' }}
                onClick={() => setShowMenu(false)}
                >
                Blog
                </Link> */}
                <Link
                to="/pricing"
                className="block px-4 py-2 text-sm hover:bg-gray-100"
                style={{ color: '#2C3E50' }}
                onClick={() => setShowMenu(false)}
                >
                Tarifs
                </Link>
                <Link
                to="/profile"
                className="block px-4 py-2 text-sm hover:bg-gray-100"
                style={{ color: '#2C3E50' }}
                onClick={() => setShowMenu(false)}
                >
                Mon Profil
                </Link>
                <Link
                to="/mes-recettes"
                className="block px-4 py-2 text-sm hover:bg-gray-100"
                style={{ color: '#2C3E50' }}
                onClick={() => setShowMenu(false)}
                >
                Mes Recettes
                </Link>
                <hr className="my-2 border-gray-200" />
                <button
                onClick={handleLogout}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                style={{ color: '#E67E22' }}
                >
                Se déconnecter
                </button>
              </div>
              )}
            </div>
            </>
          ) : (
            <>
              <Link 
                to="/login" 
                className="px-6 py-2 text-white rounded-full font-medium hover:opacity-90 transition-all"
                style={{ backgroundColor: '#87A96B' }}
              >
                Se connecter
              </Link>
            </>
          )}
          </div>
        </div>
        
        {/* Barre de recherche mobile - cachée sur la homepage et sur routes non protégées */}
        {isAuthenticated && location.pathname !== '/' && (
          <div className="mt-3 md:hidden relative">
          <div className="search-bar">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: '#6c757d' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input 
            type="text" 
            placeholder="Rechercher des recettes..." 
            className="w-full"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={() => searchResults.length > 0 && setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 200)}
            style={{ color: '#2C3E50' }}
            />
          </div>

          {/* Résultats de recherche - Mobile */}
          {showResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-96 overflow-y-auto z-50">
            {searchResults.map((recipe) => (
              <div
              key={recipe.id}
              onClick={() => handleResultClick(recipe)}
              className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 mr-3">
                {recipe.imgUrlArray && recipe.imgUrlArray.length > 0 ? (
                  <img
                  src={recipe.imgUrlArray[0]}
                  alt={recipe.title}
                  className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#6c757d' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  </div>
                )}
                </div>
                <div className="flex-grow">
                <h4 className="font-medium text-sm" style={{ color: '#2C3E50' }}>{recipe.title}</h4>
                <p className="text-xs line-clamp-1" style={{ color: '#6c757d' }}>{recipe.description}</p>
                </div>
              </div>
              </div>
            ))}
            </div>
          )}
          </div>
        )}
        <div className="mt-4 flex md:hidden justify-center space-x-6">
          {navLinks.map((link) => {
            const isActive = location.pathname.startsWith(link.to);
            const isIngredientLink = link.to.startsWith('/idees-de-repas/idees-recettes');
            const externalProps = isIngredientLink
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {};
            return (
              <Link
                key={`mobile-${link.to}`}
                to={link.to}
                {...externalProps}
                className={`text-sm font-medium transition-colors ${
                  isActive ? 'text-[#E67E22]' : 'text-[#2C3E50] hover:text-[#E67E22]'
                }`}
                onClick={() => setShowMenu(false)}
              >
                {link.label}
              </Link>
            );
          })}
        </div>
        </div>
      </header>

      {/* Bottom Navigation - Mobile uniquement */}
      {isAuthenticated && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
        <div className="flex justify-around items-center py-2">
          {/* Idées de recettes */}
          <Link
          to="/idees-de-repas"
          className={`flex flex-col items-center p-2`}
          style={{ color: location.pathname.startsWith('/idees-de-repas') ? '#87A96B' : '#6c757d' }}
          >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3a6 6 0 00-6 6c0 2.577 1.667 4.773 4 5.5V17a2 2 0 104 0v-2.5c2.333-.727 4-2.923 4-5.5a6 6 0 00-6-6zm0 18a2 2 0 002-2H10a2 2 0 002 2z" />
          </svg>
          <span className="text-xs mt-1">Idées</span>
          </Link>

          {/* Inspiration */}
          <Link
          to="/inspirations-repas"
          className={`flex flex-col items-center p-2 ${
            location.pathname === '/inspirations-repas' 
            ? '' 
            : ''
          }`}
          style={{ color: location.pathname === '/inspirations-repas' ? '#87A96B' : '#6c757d' }}
          >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <span className="text-xs mt-1">Inspiration</span>
          </Link>

          {/* Cuisiner */}
          <Link
          to="/cooking-assistant"
          className={`flex flex-col items-center p-2`}
          style={{ color: location.pathname === '/cooking-assistant' ? '#87A96B' : '#6c757d' }}
          >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-xs mt-1">Cuisiner</span>
          </Link>

          {/* Mes Recettes */}
          <Link
          to="/mes-recettes"
          className={`flex flex-col items-center p-2`}
          style={{ color: location.pathname === '/mes-recettes' ? '#87A96B' : '#6c757d' }}
          >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <span className="text-xs mt-1">Mes Recettes</span>
          </Link>

          {/* Blog */}
          {/* <Link
          to="/blog"
          className={`flex flex-col items-center p-2`}
          style={{ color: location.pathname.startsWith('/blog') ? '#87A96B' : '#6c757d' }}
          >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 20h9" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
          <span className="text-xs mt-1">Blog</span>
          </Link> */}
        </div>
        </nav>
      )}
      </>
    );
}
